import {
  FunctionComponent,
  SVGProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { useChampionshipPicker, useScreenDetector } from '../../../hooks';
import {
  AsyncComponent,
  ConfiguredComponents,
} from '@r-and-a-shared-ui/core-component-config';
import Logo, { LogoProps } from '../../Logo/Logo';
import {
  type Option,
  DropdownButton,
} from '../../DropdownButton/DropdownButton';
import TrophyStarIcon from '../../../icons/TrophyStarIcon';
import UserIcon from '../../../icons/UserIcon';
import { Link, LinkProps } from '../../Link/Link';

import styles from './PlayerPortalsHeader.module.scss';
import {
  redirectTo,
  PLAYER_INITIALS_DROPDOWN_VALUE,
  MY_PROFILE_DROPDOWN_LABEL,
  MY_PROFILE_DROPDOWN_VALUE,
  MY_PROFILE_DROPDOWN_HREF,
  SIGN_OUT_DROPDOWN_LABEL,
  SIGN_OUT_DROPDOWN_VALUE,
} from '@r-and-a-shared-ui/utils';
import {
  Attributes,
  AUTH_CONTEXT_KEYS,
  federatedSignOut,
  useAuthContext,
} from '@r-and-a-shared-ui/auth-client';
import { usePlayerPortalsConfigContext } from '@r-and-a-shared-ui/common-ui/src/contexts';
import { PPAttributes } from 'libs/auth-client/src/types/pp-user-attribute-types';

const enum DropdownButtonIds {
  None = 'none',
  Championship = 'championship',
  MyAccount = 'myAccount',
}

export type PlayerPortalsHeaderProps = {
  title?: LinkProps;
  mobileLogo?: LogoProps;
  desktopLogo?: LogoProps;
  hideChampionshipDropdown?: boolean;
  hideMyAccount?: boolean;
  navigation?: any;
  kontentItemId?: string;
};

export const PlayerPortalsHeader = ({
  title,
  mobileLogo,
  desktopLogo,
  hideChampionshipDropdown,
  hideMyAccount,
  navigation,
  kontentItemId,
}: PlayerPortalsHeaderProps) => {
  const { isClient, isMobile, isMobileWide } = useScreenDetector();
  const [pendingChampionshipChange, startTransition] = useTransition();
  const [activeDropdown, setActiveDropdown] = useState<DropdownButtonIds>(
    DropdownButtonIds.None,
  );

  const { userState } = useAuthContext(AUTH_CONTEXT_KEYS.PP_CONTEXT_KEY);
  const { siteSettings } = usePlayerPortalsConfigContext();
  const availableChampionships =
    siteSettings?.playerPortals?.availableChampionships ?? [];
  const userChampionships = userState?.user
    ? userState?.user[PPAttributes.Championships]?.split(',')
    : [];

  const userInitials = useMemo(() => {
    if (!userState.user) return '';

    const givenName = (
      userState.user[Attributes.GivenName] as string | undefined
    )?.trim();
    const familyName = (
      userState.user[Attributes.FamilyName] as string | undefined
    )?.trim();

    const firstInitial = givenName?.[0] ?? '';
    const lastInitial = familyName?.[0] ?? '';

    const initials = (firstInitial + lastInitial).toUpperCase();

    return initials || '';
  }, [userState.user]);

  const {
    currentChampionship,
    methods: { redirectToChampionship },
  } = useChampionshipPicker();

  const handleToggle = useCallback(
    (id: DropdownButtonIds) => {
      setActiveDropdown(activeDropdown === id ? DropdownButtonIds.None : id);
    },
    [setActiveDropdown, activeDropdown],
  );

  const logo = useMemo(() => {
    return isMobile ? mobileLogo : desktopLogo;
  }, [isMobile, mobileLogo, desktopLogo]);

  const handleChampionshipChange = useCallback(
    (selected: Option) => {
      startTransition(() => {
        redirectToChampionship(selected.value);
      });
    },
    [startTransition, redirectToChampionship],
  );

  const championshipOptions: Option[] = useMemo(() => {
    if (availableChampionships.length === 0 || userChampionships.length === 0) {
      return [];
    }
    return availableChampionships
      .filter(
        champ =>
          typeof champ.kontentCollection === 'string' &&
          userChampionships.includes(champ.kontentCollection.toLowerCase()),
      )
      .map(champ => ({
        value: champ.kontentCollection!,
        label: champ.name,
      }));
  }, [availableChampionships, userChampionships]);

  const defaultChampionship: Option | undefined = useMemo(() => {
    if (!currentChampionship) {
      return undefined;
    }

    return (
      championshipOptions.find(
        champ => champ.value === currentChampionship.kontentCollection,
      ) ?? undefined
    );
  }, [championshipOptions, currentChampionship?.kontentCollection]);

  const dropdownKey = useMemo(() => {
    return `champ-${defaultChampionship?.value ?? 'none'}`;
  }, [defaultChampionship?.value]);

  const myAccountOptions: Option[] = useMemo(
    () => [
      {
        value: PLAYER_INITIALS_DROPDOWN_VALUE,
        label: userInitials,
        hidden: true,
      },
      {
        value: MY_PROFILE_DROPDOWN_VALUE,
        label: MY_PROFILE_DROPDOWN_LABEL,
        href: MY_PROFILE_DROPDOWN_HREF,
      },
      { value: SIGN_OUT_DROPDOWN_VALUE, label: SIGN_OUT_DROPDOWN_LABEL },
    ],
    [userInitials],
  );

  const handleMyAccountClick = useCallback(async (option: Option) => {
    setActiveDropdown(DropdownButtonIds.None);

    if (option.value === SIGN_OUT_DROPDOWN_VALUE) {
      try {
        await federatedSignOut(true);
      } catch (e) {
        console.error('Error during sign out', e);
        redirectTo('/');
      }
    }

    if (option.href) {
      redirectTo(option.href);
    }
  }, []);

  return (
    <header
      className={styles.header}
      data-testid={'Header'}
      id="header"
      data-kontent-item-id={kontentItemId}
    >
      <div className={styles.wrapper}>
        <div className={styles['logo-wrapper']}>
          <span className={styles['logo-img']}>
            <Logo
              key={logo?.logoDescription}
              resource={logo?.resource}
              logoDescription={logo?.logoDescription}
              logoImage={logo?.logoImage}
            />
          </span>

          {title && (
            <span className={styles['logo-title']}>
              <Link {...title} hideIcon />
            </span>
          )}
        </div>

        <div className={styles['menu-wrapper']}>
          {isClient && !isMobileWide && !hideChampionshipDropdown && (
            <div className={styles['championship-picker']}>
              <DropdownButton
                key={dropdownKey}
                options={championshipOptions}
                defaultValue={defaultChampionship}
                SvgIcon={
                  TrophyStarIcon as unknown as FunctionComponent<
                    SVGProps<SVGSVGElement>
                  >
                }
                disabled={pendingChampionshipChange}
                isOpen={activeDropdown === DropdownButtonIds.Championship}
                onToggle={() => handleToggle(DropdownButtonIds.Championship)}
                onClick={champ => handleChampionshipChange(champ)}
              />
            </div>
          )}
          {!hideMyAccount && (
            <div className={'my-account'}>
              <DropdownButton
                key={userInitials}
                options={myAccountOptions}
                defaultValue={myAccountOptions[0]}
                SvgIcon={
                  UserIcon as unknown as FunctionComponent<
                    SVGProps<SVGSVGElement>
                  >
                }
                isLocked
                isOpen={activeDropdown === DropdownButtonIds.MyAccount}
                onToggle={() => handleToggle(DropdownButtonIds.MyAccount)}
                onClick={handleMyAccountClick}
              />
            </div>
          )}

          <AsyncComponent
            kontentItem={navigation}
            contentType={navigation?.system?.type as ConfiguredComponents}
            key={navigation?.system?.id}
            shouldFetchData={true}
            depthParameter={10}
          />
        </div>

        {isClient && isMobileWide && !hideChampionshipDropdown && (
          <div className={styles['championship-picker']}>
            <DropdownButton
              key={dropdownKey}
              options={championshipOptions}
              defaultValue={defaultChampionship}
              SvgIcon={
                TrophyStarIcon as unknown as FunctionComponent<
                  SVGProps<SVGSVGElement>
                >
              }
              isOpen={activeDropdown === DropdownButtonIds.Championship}
              disabled={pendingChampionshipChange}
              onToggle={() => handleToggle(DropdownButtonIds.Championship)}
              onClick={champ => handleChampionshipChange(champ)}
            />
          </div>
        )}
      </div>
    </header>
  );
};
